import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { FAMILY_FEATURES, COUPLE_PACKAGES } from '@/data/rewardsData';
import { Heart, Users, Gift, Calendar, Sparkles, PartyPopper } from 'lucide-react';

const featureIcons = [Users, Sparkles, Gift, Calendar, PartyPopper, Heart];

export const FamilySection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section id="family" ref={ref} className="relative py-24 lg:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Royal ornamental divider */}
        <div className="flex items-center gap-3 justify-center mb-10">
          <div className="h-px w-16 bg-gold/20" />
          <svg width="16" height="16" viewBox="0 0 20 20" className="text-gold/40">
            <path d="M10 0L12 8L20 10L12 12L10 20L8 12L0 10L8 8Z" fill="currentColor" />
          </svg>
          <div className="h-px w-16 bg-gold/20" />
        </div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16 lg:mb-20"
        >
          <span className="font-body text-xs tracking-[0.3em] uppercase text-gold mb-4 block">Leor Together</span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-foreground font-medium leading-tight">
            Glow as a <span className="text-shimmer-gold italic">Family</span>
          </h2>
          <p className="font-body text-base text-muted-foreground mt-4 max-w-xl mx-auto leading-relaxed">
            Create a family group (Lustre+ members) with up to 4 members. Share points, schedule together, and unlock exclusive couple & family packages.
          </p>
        </motion.div>

        {/* Family Features Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5 mb-16">
          {FAMILY_FEATURES.map((feature, i) => {
            const Icon = featureIcons[i] || Heart;
            return (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
              >
                <Card className="bg-gradient-card border-border/40 rounded-xl p-6 hover:border-gold/15 transition-all duration-500 group h-full flex flex-col">
                  <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center mb-4 group-hover:bg-gold/15 transition-colors duration-300">
                    <Icon className="w-5 h-5 text-gold" />
                  </div>
                  <h3 className="font-display text-lg text-foreground mb-2">{feature.title}</h3>
                  <p className="font-body text-xs text-muted-foreground leading-relaxed flex-1">{feature.desc}</p>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Couple Packages */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <h3 className="font-display text-2xl text-foreground text-center mb-8">Couple Packages</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {COUPLE_PACKAGES.map((pkg, i) => (
              <motion.div
                key={pkg.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
              >
                <Card className="bg-gradient-card border-border/40 rounded-xl p-5 hover:border-gold/15 transition-all duration-500 group h-full flex flex-col">
                  <div className="flex items-center gap-2 mb-3">
                    <Heart className="w-4 h-4 text-gold" />
                    <h4 className="font-display text-base text-foreground">{pkg.name}</h4>
                  </div>
                  <p className="font-body text-xs text-muted-foreground leading-relaxed flex-1 mb-3">{pkg.desc}</p>
                  <div className="mt-auto inline-flex items-center self-start px-3 py-1 rounded-full bg-gold/10 border border-gold/15">
                    <span className="font-body text-[10px] tracking-wide text-gold">{pkg.earn}</span>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
